import React, { useState } from 'react';
import { Controller, Control, FieldErrors, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { PlusCircleIcon, LockClosedIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { RequiredIndicator, CurrencyInput } from '@/components/shared';
import { useSalaryPermissions } from '@/features/Collaborators/hooks/useSalaryPermissions';
import { useSalaryMutation } from '@/features/Collaborators/hooks/data/useSalaryMutation';

interface NewSalaryEntryFieldsetProps {
    context: CollaboratorFormContext;
    control: Control<CollaboratorFormData>;
    setValue: UseFormSetValue<CollaboratorFormData>;
    watch: UseFormWatch<CollaboratorFormData>;
    errors: FieldErrors<CollaboratorFormData>;
    isSubmitting?: boolean;
    collaboratorId?: string;
}

const NewSalaryEntryFieldset: React.FC<NewSalaryEntryFieldsetProps> = ({
    context,
    control,
    setValue,
    watch,
    errors,
    isSubmitting = false,
    collaboratorId
}) => {
    // Helper para verificar se campo deve aparecer
    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);

    // Helper local para verificar se campos são obrigatórios
    const isRequired = (fieldName: string) => collaboratorSchemaHelpers.isFieldRequired(fieldName, context);

    // Permission hooks
    const {
        canViewSalary,
        canEditSalary,
        shouldShowAsDisabled
    } = useSalaryPermissions();

    // Salary mutation hook
    const { updateSalary, updateHistory } = useSalaryMutation(collaboratorId || '');

    // Estado local para validação de entrada salarial
    const [localValidationErrors, setLocalValidationErrors] = useState<Record<string, string>>({});
    const [isAddingEntry, setIsAddingEntry] = useState(false);

    // Limpar erro específico quando usuário começar a editar
    const clearFieldError = (fieldName: string) => {
        if (localValidationErrors[fieldName]) {
            setLocalValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            });
        }
    };

    // Early return se não deve mostrar neste contexto
    if (!shouldShow('newSalaryEntryDate') && !shouldShow('newSalaryEntryAmount') && !shouldShow('newSalaryEntryReason')) {
        return null;
    }

    // Check permission to view/edit salary
    if (!canViewSalary && context !== 'PROFILE_UPDATE') {
        return (
            <fieldset data-testid="new-salary-entry-fieldset" className="border p-4 rounded-md bg-gray-50">
                <legend className="text-sm font-medium px-1 text-gray-400">Novo Registro Salarial</legend>
                <div className="mt-4 flex items-center justify-center text-gray-500">
                    <LockClosedIcon className="h-5 w-5 mr-2" />
                    <span className="text-sm">Você não tem permissão para adicionar registros salariais</span>
                </div>
            </fieldset>
        );
    }

    const isReadOnly = !canEditSalary && context !== 'PROFILE_UPDATE';

    // Watch para campos condicionais
    const formData = watch();
    const editingHistoryId = formData.salaryHistoryEditingId || null;
    const editingIsSaved = formData.salaryHistoryEditingIsSaved || false;
    const editingUnsavedIndex = formData.salaryHistoryEditingUnsavedIndex ?? null;
    const isEditing = Boolean(editingIsSaved ? editingHistoryId : editingUnsavedIndex !== null);

    // Opções de motivo
    const salaryReasons = [
        'Salário Inicial',
        'Reajuste após periodo de experiência',
        'Mérito',
        'Dissídio',
        'Troca de Projeto',
        'Troca de Função',
        'Outros'
    ];

    // Handler para alternar tipo de salário
    const handleSalaryTypeToggle = () => {
        if (isReadOnly) return;
        const currentType = formData.newSalaryEntrySalaryType || 'CLT';
        setValue('newSalaryEntrySalaryType', currentType === 'CLT' ? 'PJ' : 'CLT');
    };

    // Validação local manual para adicionar entrada
    const validateLocalEntry = () => {
        const errors: Record<string, string> = {};

        // Obter dados atuais
        const date = formData.newSalaryEntryDate;
        const amount = formData.newSalaryEntryAmount;
        const reason = formData.newSalaryEntryReason;

        // Validar campos obrigatórios
        if (!date) {
            errors.date = 'Data é obrigatória para adicionar entrada salarial';
        }

        if (!amount || amount <= 0) {
            errors.amount = 'Salário deve ser maior que zero';
        }

        if (!reason) {
            errors.reason = 'Motivo é obrigatório para adicionar entrada salarial';
        }

        setLocalValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handler para adicionar entrada
    const resetEntryFields = () => {
        setValue('newSalaryEntryDate', '');
        setValue('newSalaryEntryAmount', 0);
        setValue('newSalaryEntryExtras', 0);
        setValue('newSalaryEntrySalaryType', 'CLT');
        setValue('newSalaryEntryReason', '');
        setValue('newSalaryEntryObservations', '');
        setValue('salaryHistoryEditingId', null);
        setValue('salaryHistoryEditingIsSaved', false);
        setValue('salaryHistoryEditingUnsavedIndex', null);
    };

    const handleAddEntry = async () => {
        if (isReadOnly) return;

        // Validar localmente
        if (!validateLocalEntry()) {
            return;
        }

        setIsAddingEntry(true);

        try {
            // Obter dados atuais
            const date = formData.newSalaryEntryDate;
            const amount = formData.newSalaryEntryAmount;
            const reason = formData.newSalaryEntryReason;
            const salaryType = formData.newSalaryEntrySalaryType || 'CLT';
            const observations = formData.newSalaryEntryObservations || '';

            const payload = {
                amount: Number(amount),
                extras: Number(formData.newSalaryEntryExtras) || 0,
                salaryType: salaryType as 'CLT' | 'PJ' | 'Estagiário' | 'Sócio',
                changeDate: date as string,
                changeReason: reason as string,
                changeObservations: observations
            };

            if (isEditing && editingIsSaved && editingHistoryId) {
                await updateHistory.mutateAsync({
                    historyId: editingHistoryId,
                    data: payload
                });
            } else if (isEditing && editingUnsavedIndex !== null) {
                const currentSalaries = formData.salaryHistorySalaries || [];
                const updatedSalaries = currentSalaries.map((entry: any, idx: number) =>
                    idx === editingUnsavedIndex
                        ? {
                            ...entry,
                            date: payload.changeDate,
                            amount: payload.amount,
                            extras: payload.extras,
                            salaryType: payload.salaryType,
                            reason: payload.changeReason,
                            observations: payload.changeObservations ?? ''
                        }
                        : entry
                );
                setValue('salaryHistorySalaries', updatedSalaries);
            } else if (collaboratorId && updateSalary) {
                await updateSalary.mutateAsync({
                    data: {
                        amount: payload.amount,
                        extras: payload.extras,
                        salaryType: payload.salaryType,
                        effectiveDate: payload.changeDate
                    },
                    reason: payload.changeReason,
                    observations: payload.changeObservations
                });
            } else {
                // Fallback to local form state (for new collaborators)
                // NOTE: Don't add 'id' field here! The absence of 'id' signals
                // that this is a new entry that needs to be saved to Firestore.
                // If we add a temporary ID, the save logic will skip it thinking it already exists.
                const newEntry = {
                    date: payload.changeDate,
                    amount: payload.amount,
                    extras: payload.extras,
                    salaryType: payload.salaryType as string,
                    reason: payload.changeReason as string,
                    observations: payload.changeObservations ?? ''
                };

                // Obter histórico atual
                const currentSalaries = formData.salaryHistorySalaries || [];

                // Adicionar nova entrada ao histórico
                const updatedSalaries = [...currentSalaries, newEntry];
                setValue('salaryHistorySalaries', updatedSalaries);
            }

            resetEntryFields();

            // Limpar erros locais
            setLocalValidationErrors({});

        } catch (error) {
            console.error('Erro ao adicionar entrada salarial:', error);
        } finally {
            setIsAddingEntry(false);
        }
    };

    return (
        <fieldset data-testid="new-salary-entry-fieldset" className="border p-4 rounded-md">
            <legend className="text-sm font-medium px-1 text-gray-400">
                Novo Registro Salarial
                {isReadOnly && (
                    <span className="ml-2 text-xs text-gray-500">(Somente leitura)</span>
                )}
            </legend>
            {isEditing && (
                <div className="mt-2 flex items-center justify-between rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-800">
                    <span>Editando registro {editingIsSaved ? 'salvo' : 'novo'}...</span>
                    <button
                        type="button"
                        onClick={resetEntryFields}
                        className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900"
                    >
                        <XMarkIcon className="h-4 w-4" />
                        Cancelar edição
                    </button>
                </div>
            )}
            <div className="space-y-4 pt-2">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-start">

                    {/* Data */}
                    {shouldShow('newSalaryEntryDate') && (
                        <div className="lg:col-span-1">
                            <label htmlFor="newSalaryDate" className="block text-sm font-medium text-gray-700">
                                Data
                                <RequiredIndicator show={true} />
                            </label>
                            <Controller
                                name="newSalaryEntryDate"
                                control={control}
                                render={({ field }) => (
                                    <>
                                        <input
                                            {...field}
                                            value={field.value || ''}
                                            type="date"
                                            id="newSalaryDate"
                                            className="input-primary w-full mt-1"
                                            disabled={isReadOnly || isSubmitting}
                                            onChange={(e) => {
                                                field.onChange(e);
                                                clearFieldError('date');
                                            }}
                                        />
                                        {localValidationErrors.date && (
                                            <span className="text-red-500 text-sm mt-1">{localValidationErrors.date}</span>
                                        )}
                                    </>
                                )}
                            />
                        </div>
                    )}

                    {/* Salário */}
                    {shouldShow('newSalaryEntryAmount') && (
                        <div className="lg:col-span-1">
                            <label htmlFor="newSalaryAmount" className="block text-sm font-medium text-gray-700">
                                Salário
                                <RequiredIndicator show={true} />
                            </label>
                            <Controller
                                name="newSalaryEntryAmount"
                                control={control}
                                render={({ field }) => (
                                    <>
                                        <CurrencyInput
                                            value={field.value}
                                            onValueChange={(value, name, values) => {
                                                if (!isReadOnly) {
                                                    field.onChange(values?.float || 0);
                                                    clearFieldError('amount');
                                                }
                                            }}
                                            id="newSalaryAmount"
                                            name="newSalaryAmount"
                                            label=""
                                            placeholder="0,00"
                                            disabled={isReadOnly || isSubmitting}
                                        />
                                        {localValidationErrors.amount && (
                                            <span className="text-red-500 text-sm mt-1">{localValidationErrors.amount}</span>
                                        )}
                                    </>
                                )}
                            />
                        </div>
                    )}

                    {/* Extras/Caju */}
                    {shouldShow('newSalaryEntryExtras') && (
                        <div className="lg:col-span-1">
                            <label htmlFor="newSalaryExtras" className="block text-sm font-medium text-gray-700">
                                Extras/Caju
                            </label>
                            <Controller
                                name="newSalaryEntryExtras"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <>
                                        <CurrencyInput
                                            value={field.value}
                                            onValueChange={(value, name, values) => {
                                                if (!isReadOnly) {
                                                    field.onChange(values?.float || 0);
                                                }
                                            }}
                                            id="newSalaryExtras"
                                            name="newSalaryExtras"
                                            label=""
                                            placeholder="0,00"
                                            disabled={isReadOnly || isSubmitting}
                                        />
                                        {fieldState.error && (
                                            <span className="text-red-500 text-sm mt-1">{fieldState.error.message}</span>
                                        )}
                                    </>
                                )}
                            />
                        </div>
                    )}

                    {/* Motivo */}
                    {shouldShow('newSalaryEntryReason') && (
                        <div className="lg:col-span-1">
                            <label htmlFor="newSalaryReason" className="block text-sm font-medium text-gray-700">
                                Motivo
                                <RequiredIndicator show={true} />
                            </label>
                            <Controller
                                name="newSalaryEntryReason"
                                control={control}
                                render={({ field }) => (
                                    <>
                                        <select
                                            {...field}
                                            value={field.value || ''}
                                            id="newSalaryReason"
                                            className="input-primary w-full mt-1"
                                            disabled={isReadOnly || isSubmitting}
                                            onChange={(e) => {
                                                field.onChange(e);
                                                clearFieldError('reason');
                                            }}
                                        >
                                            <option value="">Selecione o motivo</option>
                                            {salaryReasons.map(reason => (
                                                <option key={reason} value={reason}>{reason}</option>
                                            ))}
                                        </select>
                                        {localValidationErrors.reason && (
                                            <span className="text-red-500 text-sm mt-1">{localValidationErrors.reason}</span>
                                        )}
                                    </>
                                )}
                            />
                        </div>
                    )}

                    {/* Tipo de Contrato */}
                    {shouldShow('newSalaryEntrySalaryType') && (
                        <div className="lg:col-span-1">
                            <label className="block text-sm font-medium text-gray-700">
                                Tipo de Contrato
                                <RequiredIndicator show={true} />
                            </label>
                            <div className="mt-2 flex items-center">
                                <span className={`mr-3 text-sm font-medium ${(formData.newSalaryEntrySalaryType || 'CLT') === 'CLT' ? 'text-indigo-600' : 'text-gray-500'}`}>
                                    CLT
                                </span>
                                <button
                                    type="button"
                                    onClick={handleSalaryTypeToggle}
                                    disabled={isReadOnly || isSubmitting}
                                    className={`${(formData.newSalaryEntrySalaryType || 'CLT') === 'PJ' ? 'bg-indigo-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
                                >
                                    <span className={`${(formData.newSalaryEntrySalaryType || 'CLT') === 'PJ' ? 'translate-x-5' : 'translate-x-0'} inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`} />
                                </button>
                                <span className={`ml-3 text-sm font-medium ${(formData.newSalaryEntrySalaryType || 'CLT') === 'PJ' ? 'text-indigo-600' : 'text-gray-500'}`}>
                                    PJ
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Botão Adicionar */}
                    <div className="lg:col-span-1">
                        <div className="mt-6">
                            <button
                                type="button"
                                onClick={handleAddEntry}
                                disabled={isReadOnly || isSubmitting || isAddingEntry}
                                className={`flex items-center gap-2 px-4 py-2 border rounded-md transition-colors w-full justify-center ${
                                    isReadOnly
                                        ? 'text-gray-400 border-gray-300 bg-gray-50 cursor-not-allowed'
                                        : 'text-indigo-600 border-indigo-600 hover:bg-indigo-50'
                                }`}
                            >
                                {isReadOnly ? (
                                    <>
                                        <LockClosedIcon className="h-5 w-5" />
                                        Bloqueado
                                    </>
                                ) : (
                                    <>
                                        <PlusCircleIcon className="h-5 w-5" />
                                        {isAddingEntry
                                            ? 'Salvando...'
                                            : isEditing
                                                ? 'Salvar alterações'
                                                : 'Adicionar'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Observações */}
                    {shouldShow('newSalaryEntryObservations') && (
                        <div className="col-span-full">
                            <label htmlFor="newSalaryObservations" className="block text-sm font-medium text-gray-700">
                                Observações
                            </label>
                            <Controller
                                name="newSalaryEntryObservations"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <>
                                        <textarea
                                            {...field}
                                            value={field.value || ''}
                                            id="newSalaryObservations"
                                            rows={2}
                                            className="input-primary w-full mt-1"
                                            placeholder="Observações sobre o registro salarial..."
                                            disabled={isReadOnly || isSubmitting}
                                        />
                                        {fieldState.error && (
                                            <span className="text-red-500 text-sm mt-1">{fieldState.error.message}</span>
                                        )}
                                    </>
                                )}
                            />
                        </div>
                    )}
                </div>

                {/* Permission notice if user can view but not edit */}
                {canViewSalary && !canEditSalary && context !== 'PROFILE_UPDATE' && (
                    <div className="text-xs text-gray-500 mt-4 flex items-center">
                        <LockClosedIcon className="h-3 w-3 mr-1" />
                        Você pode visualizar mas não editar registros salariais
                    </div>
                )}
            </div>
        </fieldset>
    );
};

export default NewSalaryEntryFieldset;
